import { z } from 'zod';

import { dashboardT } from '../../lib/messages';
import {
  supplyAppSelectionSchema,
  supplyDomainSelectionSchema,
  tailoredSupplySelectionSchema,
} from '../campaigns/supply/model';
import { TargetingSupplyType, type PersistedTargeting } from './targetingTypes';
import {
  contextualNames,
  contextualValues,
  legacyIds,
  normalizeContextualSelection,
  normalizeLegacyExcludedItems,
  normalizeLegacyLiverampSegments,
  normalizePersistedDomains,
  normalizePersistedGeoLocations,
  parseOptionalNumber,
  resolveHomogeneousInclusion,
  splitLines,
  splitZipCodes,
  stripSupplyAppDisplayMetadata,
  stringifyNumber,
  targetingDeviceTypeId,
} from './modelHelpers';
import { TargetingConsumer } from './config';

export * from './targetingTypes';

const namedTargetingEntitySchema = z.object({ id: z.string(), name: z.string() });
const liverampSegmentSchema = z.object({
  id: z.string(),
  segmentId: z.string().optional(),
  segmentName: z.string().optional(),
  providerName: z.string().optional(),
  description: z.string().nullish(),
  segmentDescription: z.string().nullish(),
  useRestrictions: z.string().nullish(),
  permittedAdvertisers: z.string().nullish(),
  customSegment: z.boolean().optional(),
  digitalCpm: z.number().nullish(),
  tvCpm: z.number().nullish(),
});
const targetingLanguageSchema = namedTargetingEntitySchema.extend({ code: z.string().optional() });
export const targetingPlacementSchema = namedTargetingEntitySchema.extend({
  deliveryType: z.string().nullish(),
  bidFloor: z.number().nullish(),
  cpm: z.number().nullish(),
});
const targetingLocationSchema = z
  .object({
    excluded: z.boolean(),
    type: z.string(),
    continentId: z.string().optional(),
    countryId: z.string().optional(),
    countryName: z.string().optional(),
    geocodeId: z.string().optional(),
    regionId: z.string().optional(),
    subRegionId: z.string().optional(),
    subdivision1Name: z.string().optional(),
    subdivision2Name: z.string().optional(),
    locationName: z.string().optional(),
    tag: z.string().optional(),
  })
  .loose();
const optionalPercentSchema = z.string().superRefine((value, context) => {
  const parsed = parseOptionalNumber(value);
  if (value.trim() && parsed === undefined) {
    context.addIssue({ code: 'custom', message: dashboardT('targeting.validation.invalidNumber') });
    return;
  }
  if (parsed !== undefined && (parsed < 0 || parsed > 100)) {
    context.addIssue({ code: 'custom', message: dashboardT('targeting.validation.percentRange') });
  }
});

export const targetingFormSchema = z.object({
  deviceTypeIds: z.array(z.string()),
  excludedDeviceTypeIds: z.array(z.string()),
  platformIds: z.array(z.string()),
  platformsExcluded: z.boolean(),
  includeBundles: z.boolean(),
  includeIps: z.boolean(),
  reqWithoutDeviceIfa: z.boolean(),
  reqWithoutAppStoreUrl: z.boolean(),
  reqFilterOutInstlNotEq1: z.boolean(),
  reqWithoutContentChannel: z.boolean(),
  reqWithoutContentNetwork: z.boolean(),
  pmpDealIdsText: z.string(),
  pmpDealIdsExcluded: z.boolean(),
  xp1: z.string(),
  xp2: z.string(),
  xp3: z.string(),
  xp4: z.string(),
  xpExcluded: z.boolean(),
  geoLocations: z.array(targetingLocationSchema),
  zipCodesText: z.string(),
  zipCodesIncluded: z.boolean(),
  supplyType: z.enum(TargetingSupplyType),
  appsIncluded: z.boolean(),
  includedApps: z.array(supplyAppSelectionSchema),
  excludedApps: z.array(supplyAppSelectionSchema),
  placementsIncluded: z.boolean(),
  includedPlacements: z.array(targetingPlacementSchema),
  excludedPlacements: z.array(targetingPlacementSchema),
  domainsIncluded: z.boolean(),
  domainsText: z.string(),
  includedDomains: z.array(supplyDomainSelectionSchema),
  excludedDomains: z.array(supplyDomainSelectionSchema),
  tailoredSuppliesIncluded: z.boolean(),
  includedTailoredSupplies: z.array(tailoredSupplySelectionSchema),
  excludedTailoredSupplies: z.array(tailoredSupplySelectionSchema),
  liverampSegmentsIncluded: z.boolean(),
  liverampTargetingGroupId: z.string(),
  liverampSegments: z.array(liverampSegmentSchema),
  tailoredSupplyExcludedApps: z.array(supplyAppSelectionSchema),
  tailoredSupplyExcludedDomains: z.array(supplyDomainSelectionSchema),
  iabCategoriesIncluded: z.boolean(),
  iabCategoryIds: z.array(z.string()),
  contextualChannelsIncluded: z.boolean(),
  contextualChannelsText: z.string(),
  contextualChannelGroupsIncluded: z.boolean(),
  contextualChannelGroupsText: z.string(),
  contextualContentTitlesIncluded: z.boolean(),
  contextualContentTitlesText: z.string(),
  contextualContentKeywordsIncluded: z.boolean(),
  contextualContentKeywordsText: z.string(),
  contextualContentGenresIncluded: z.boolean(),
  contextualContentGenresText: z.string(),
  viewability: optionalPercentSchema,
  ctr: optionalPercentSchema,
  supplyCompanies: z.array(namedTargetingEntitySchema),
  userLanguages: z.array(targetingLanguageSchema),
});

export type TargetingFormValue = z.infer<typeof targetingFormSchema>;

export const defaultTargetingFormValue: TargetingFormValue = {
  deviceTypeIds: [],
  excludedDeviceTypeIds: [],
  platformIds: [],
  platformsExcluded: false,
  includeBundles: true,
  includeIps: true,
  reqWithoutDeviceIfa: false,
  reqWithoutAppStoreUrl: false,
  reqFilterOutInstlNotEq1: false,
  reqWithoutContentChannel: false,
  reqWithoutContentNetwork: false,
  pmpDealIdsText: '',
  pmpDealIdsExcluded: false,
  xp1: '',
  xp2: '',
  xp3: '',
  xp4: '',
  xpExcluded: false,
  geoLocations: [],
  zipCodesText: '',
  zipCodesIncluded: true,
  supplyType: TargetingSupplyType.Apps,
  appsIncluded: true,
  includedApps: [],
  excludedApps: [],
  placementsIncluded: true,
  includedPlacements: [],
  excludedPlacements: [],
  domainsIncluded: true,
  domainsText: '',
  includedDomains: [],
  excludedDomains: [],
  tailoredSuppliesIncluded: true,
  includedTailoredSupplies: [],
  excludedTailoredSupplies: [],
  liverampSegmentsIncluded: true,
  liverampTargetingGroupId: '',
  liverampSegments: [],
  tailoredSupplyExcludedApps: [],
  tailoredSupplyExcludedDomains: [],
  iabCategoriesIncluded: true,
  iabCategoryIds: [],
  contextualChannelsIncluded: true,
  contextualChannelsText: '',
  contextualChannelGroupsIncluded: true,
  contextualChannelGroupsText: '',
  contextualContentTitlesIncluded: true,
  contextualContentTitlesText: '',
  contextualContentKeywordsIncluded: true,
  contextualContentKeywordsText: '',
  contextualContentGenresIncluded: true,
  contextualContentGenresText: '',
  viewability: '',
  ctr: '',
  supplyCompanies: [],
  userLanguages: [],
};

const targetingSupplyTypesByValue: Readonly<Record<string, TargetingSupplyType>> = {
  [TargetingSupplyType.All]: TargetingSupplyType.All,
  [TargetingSupplyType.Apps]: TargetingSupplyType.Apps,
  [TargetingSupplyType.Domains]: TargetingSupplyType.Domains,
};

export function createTargetingFormValue(
  targeting: PersistedTargeting | null | undefined,
  consumer?: TargetingConsumer,
): TargetingFormValue {
  if (!targeting) {
    return structuredClone(defaultTargetingFormValue);
  }
  const iabCategories = targeting.iabCategories ?? [];
  const deviceTypes = targeting.deviceTypes ?? [];
  const platforms = targeting.platforms ?? [];
  const platformsIncluded = resolveHomogeneousInclusion(platforms, 'platform');
  const pmpDealIds = normalizeLegacyExcludedItems(targeting.pmpDealIds);
  const pmpDealIdsIncluded = resolveHomogeneousInclusion(pmpDealIds, 'PMP deal ID');
  const zipCodes = targeting.zipCodes ?? [];
  const zipCodesIncluded = resolveHomogeneousInclusion(zipCodes, 'ZIP code');
  const iabCategoriesIncluded = resolveHomogeneousInclusion(iabCategories, 'IAB category');
  const legacyLiveramp = normalizeLegacyLiverampSegments(targeting.targetingLRSegments);
  const domains = normalizePersistedDomains(targeting);
  const domainsIncluded =
    consumer && consumer !== TargetingConsumer.LineItem
      ? resolveHomogeneousInclusion(
          [
            ...domains.included.map(() => ({ excluded: false })),
            ...domains.excluded.map(() => ({ excluded: true })),
          ],
          'domain',
        )
      : domains.excluded.length === 0;
  const placements = normalizePersistedPlacements(targeting);
  const channels = normalizeContextualSelection(
    targeting.includedChannels,
    targeting.excludedChannels,
  );
  const channelGroups = normalizeContextualSelection(
    targeting.includedChannelGroups,
    targeting.excludedChannelGroups,
  );
  const contentTitles = normalizeContextualSelection(
    targeting.includedContentTitles,
    targeting.excludedContentTitles,
  );
  const contentKeywords = normalizeContextualSelection(
    targeting.includedContentKeywords,
    targeting.excludedContentKeywords,
  );
  const contentGenres = normalizeContextualSelection(
    targeting.includedContentGenres,
    targeting.excludedContentGenres,
  );
  return targetingFormSchema.parse({
    deviceTypeIds: deviceTypes
      .filter((deviceType) => !deviceType.excluded)
      .map(targetingDeviceTypeId)
      .filter(Boolean),
    excludedDeviceTypeIds: deviceTypes
      .filter((deviceType) => deviceType.excluded)
      .map(targetingDeviceTypeId)
      .filter(Boolean),
    platformIds: platforms
      .map((platform) => platform.platform ?? platform.id ?? '')
      .filter(Boolean),
    platformsExcluded: !platformsIncluded,
    includeBundles: targeting.includeBundles ?? true,
    includeIps: targeting.includeIps ?? true,
    reqWithoutDeviceIfa: targeting.reqWithoutDeviceIfa ?? false,
    reqWithoutAppStoreUrl: targeting.reqWithoutAppStoreUrl ?? false,
    reqFilterOutInstlNotEq1: targeting.reqFilterOutInstlNotEq1 ?? false,
    reqWithoutContentChannel: targeting.reqWithoutContentChannel ?? false,
    reqWithoutContentNetwork: targeting.reqWithoutContentNetwork ?? false,
    pmpDealIdsText: legacyIds(targeting.pmpDealIds).join('\n'),
    pmpDealIdsExcluded: !pmpDealIdsIncluded,
    xp1: targeting.xp1 ?? '',
    xp2: targeting.xp2 ?? '',
    xp3: targeting.xp3 ?? '',
    xp4: targeting.xp4 ?? '',
    xpExcluded: targeting.xpExcluded ?? false,
    geoLocations: normalizePersistedGeoLocations(targeting.geoLocations),
    zipCodesText: zipCodes.map((item) => item.zipCode).join('\n'),
    zipCodesIncluded,
    supplyType: normalizePersistedSupplyType(targeting.supplyType, consumer),
    appsIncluded: !targeting.excludedApps?.length,
    includedApps: [...(targeting.includedApps ?? targeting.appStoreIds ?? [])],
    excludedApps: [...(targeting.excludedApps ?? [])],
    placementsIncluded: placements.excluded.length === 0,
    includedPlacements: placements.included,
    excludedPlacements: placements.excluded,
    domainsIncluded,
    domainsText: (domains.excluded.length ? domains.excluded : domains.included)
      .map((domain) => domain.url)
      .join('\n'),
    includedDomains: domains.included,
    excludedDomains: domains.excluded,
    tailoredSuppliesIncluded: !targeting.excludedTailoredSupplies?.length,
    includedTailoredSupplies: [...(targeting.includedTailoredSupplies ?? [])],
    excludedTailoredSupplies: [...(targeting.excludedTailoredSupplies ?? [])],
    liverampSegmentsIncluded: !legacyLiveramp.excluded,
    liverampTargetingGroupId: legacyLiveramp.id,
    liverampSegments: legacyLiveramp.segments,
    tailoredSupplyExcludedApps: [...(targeting.tailoredSupplyExcludedApps ?? [])],
    tailoredSupplyExcludedDomains: [...(targeting.tailoredSupplyExcludedDomains ?? [])],
    iabCategoriesIncluded,
    iabCategoryIds: iabCategories.map((item) => item.iabCategory).filter(Boolean),
    contextualChannelsIncluded: channels.included,
    contextualChannelsText: channels.names.join('\n'),
    contextualChannelGroupsIncluded: channelGroups.included,
    contextualChannelGroupsText: channelGroups.names.join('\n'),
    contextualContentTitlesIncluded: contentTitles.included,
    contextualContentTitlesText: contentTitles.names.join('\n'),
    contextualContentKeywordsIncluded: contentKeywords.included,
    contextualContentKeywordsText: contentKeywords.names.join('\n'),
    contextualContentGenresIncluded: contentGenres.included,
    contextualContentGenresText: contentGenres.names.join('\n'),
    viewability: stringifyNumber(targeting.viewability),
    ctr: stringifyNumber(targeting.ctr),
    supplyCompanies: [...(targeting.supplyCompanies ?? [])],
    userLanguages: [...(targeting.userLanguages ?? [])],
  });
}

export function serializeTargetingFormValue(
  value: TargetingFormValue,
  consumer: TargetingConsumer = TargetingConsumer.LineItem,
): PersistedTargeting {
  const classicDomains = splitLines(value.domainsText).map((domain) => ({
    id: domain,
    url: domain,
  }));
  const usesClassicDomains = consumer !== TargetingConsumer.LineItem;
  const selectedDomains = usesClassicDomains ? classicDomains : value.includedDomains;
  const deselectedDomains = usesClassicDomains ? classicDomains : value.excludedDomains;
  const includedDeviceTypeIds = new Set(value.deviceTypeIds);

  return {
    deviceTypes: [
      ...value.deviceTypeIds.map((deviceTypeId) => ({ deviceTypeId, excluded: false })),
      ...value.excludedDeviceTypeIds
        .filter((deviceTypeId) => !includedDeviceTypeIds.has(deviceTypeId))
        .map((deviceTypeId) => ({ deviceTypeId, excluded: true })),
    ],
    platforms: value.platformIds.map((platform) => ({
      platform,
      excluded: value.platformsExcluded,
    })),
    includeBundles: value.includeBundles,
    includeIps: value.includeIps,
    reqWithoutDeviceIfa: value.reqWithoutDeviceIfa,
    reqWithoutAppStoreUrl: value.reqWithoutAppStoreUrl,
    reqFilterOutInstlNotEq1: value.reqFilterOutInstlNotEq1,
    reqWithoutContentChannel: value.reqWithoutContentChannel,
    reqWithoutContentNetwork: value.reqWithoutContentNetwork,
    pmpDealIds: splitLines(value.pmpDealIdsText).map((id) => ({
      id,
      excluded: value.pmpDealIdsExcluded,
    })),
    xp1: value.xp1 || null,
    xp2: value.xp2 || null,
    xp3: value.xp3 || null,
    xp4: value.xp4 || null,
    xpExcluded: value.xpExcluded,
    geoLocations: value.geoLocations,
    zipCodes: splitZipCodes(value.zipCodesText, !value.zipCodesIncluded),
    supplyType: value.supplyType,
    includedApps: value.appsIncluded ? stripSupplyAppDisplayMetadata(value.includedApps) : [],
    excludedApps: value.appsIncluded ? [] : stripSupplyAppDisplayMetadata(value.excludedApps),
    includedPlacements: value.placementsIncluded ? value.includedPlacements : [],
    excludedPlacements: value.placementsIncluded ? [] : value.excludedPlacements,
    includedDomains: value.domainsIncluded ? selectedDomains : [],
    excludedDomains: value.domainsIncluded ? [] : deselectedDomains,
    includedTailoredSupplies: value.tailoredSuppliesIncluded ? value.includedTailoredSupplies : [],
    excludedTailoredSupplies: value.tailoredSuppliesIncluded ? [] : value.excludedTailoredSupplies,
    targetingLRSegments:
      value.liverampTargetingGroupId || value.liverampSegments.length
        ? {
            ...(value.liverampTargetingGroupId ? { id: value.liverampTargetingGroupId } : {}),
            excluded: !value.liverampSegmentsIncluded,
            lrSegments: value.liverampSegments,
          }
        : null,
    tailoredSupplyExcludedApps: stripSupplyAppDisplayMetadata(value.tailoredSupplyExcludedApps),
    tailoredSupplyExcludedDomains: value.tailoredSupplyExcludedDomains,
    iabCategories: value.iabCategoryIds.map((iabCategory) => ({
      iabCategory,
      excluded: !value.iabCategoriesIncluded,
    })),
    includedChannels: contextualValues(
      value.contextualChannelsText,
      value.contextualChannelsIncluded,
    ),
    excludedChannels: contextualValues(
      value.contextualChannelsText,
      !value.contextualChannelsIncluded,
      true,
    ),
    includedChannelGroups: contextualNames(
      splitLines(value.contextualChannelGroupsText),
      value.contextualChannelGroupsIncluded,
    ),
    excludedChannelGroups: contextualNames(
      splitLines(value.contextualChannelGroupsText),
      !value.contextualChannelGroupsIncluded,
      true,
    ),
    includedContentTitles: contextualValues(
      value.contextualContentTitlesText,
      value.contextualContentTitlesIncluded,
    ),
    excludedContentTitles: contextualValues(
      value.contextualContentTitlesText,
      !value.contextualContentTitlesIncluded,
      true,
    ),
    includedContentKeywords: contextualValues(
      value.contextualContentKeywordsText,
      value.contextualContentKeywordsIncluded,
    ),
    excludedContentKeywords: contextualValues(
      value.contextualContentKeywordsText,
      !value.contextualContentKeywordsIncluded,
      true,
    ),
    includedContentGenres: contextualValues(
      value.contextualContentGenresText,
      value.contextualContentGenresIncluded,
    ),
    excludedContentGenres: contextualValues(
      value.contextualContentGenresText,
      !value.contextualContentGenresIncluded,
      true,
    ),
    viewability: parseOptionalNumber(value.viewability) ?? null,
    ctr: parseOptionalNumber(value.ctr) ?? null,
    supplyCompanies: value.supplyCompanies,
    userLanguages: value.userLanguages,
  };
}

function normalizePersistedPlacements(targeting: PersistedTargeting) {
  const explicitIncluded = targeting.includedPlacements ?? [];
  const explicitExcluded = targeting.excludedPlacements ?? [];
  if (explicitIncluded.length || explicitExcluded.length) {
    return {
      included: normalizePlacementList(explicitIncluded),
      excluded: normalizePlacementList(explicitExcluded),
    };
  }
  return (targeting.placements ?? []).reduce<{
    included: z.infer<typeof targetingPlacementSchema>[];
    excluded: z.infer<typeof targetingPlacementSchema>[];
  }>(
    (result, placement) => {
      const parsed = targetingPlacementSchema.safeParse(placement);
      if (!parsed.success) return result;
      result[placement.excluded ? 'excluded' : 'included'].push(parsed.data);
      return result;
    },
    { included: [], excluded: [] },
  );
}

function normalizePlacementList(placements: readonly unknown[]) {
  return placements.flatMap((placement) => {
    const parsed = targetingPlacementSchema.safeParse(placement);
    return parsed.success ? [parsed.data] : [];
  });
}

function normalizePersistedSupplyType(
  supplyType: PersistedTargeting['supplyType'],
  consumer?: TargetingConsumer,
) {
  const normalized = targetingSupplyTypesByValue[String(supplyType)] ?? TargetingSupplyType.Apps;
  return normalized === TargetingSupplyType.All && consumer !== TargetingConsumer.LineItem
    ? TargetingSupplyType.Apps
    : normalized;
}

export function validateTargetingFormValue(value: TargetingFormValue) {
  const errors: Partial<Record<'ctr' | 'viewability', string>> = {};
  const result = targetingFormSchema.safeParse(value);
  if (!result.success) {
    for (const issue of result.error.issues) {
      const field = issue.path[0];
      if ((field === 'ctr' || field === 'viewability') && !errors[field]) {
        errors[field] = issue.message;
      }
    }
  }
  return errors;
}
